import type { WorkflowHealth, WorkflowTestStatus, WorkflowTestStreamEvent } from '@chatsdet/types';

export type WorkflowTestEntry = {
  workflowId: string;
  status: WorkflowTestStatus;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number;
  errorMessage?: string | null;
  skipReason?: string | null;
  linkedIssueId?: string | null;
  isQuarantined?: boolean;
  health?: WorkflowHealth;
  testRunId: string;
  workflowRunId: string | null;
  message?: string | null;
  lastUpdatedAt: string;
  resumeAt?: string | null;
  segmentIndex?: number | null;
  totalSegments?: number | null;
};

export type WorkflowTestStreamState = {
  suiteId: string;
  status: 'idle' | 'running' | 'completed';
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number;
  totalWorkflows: number;
  completedWorkflows: number;
  failedWorkflows: number;
  inconclusiveWorkflows: number;
  successfulWorkflows: number;
  skippedWorkflows: number;
  quarantinedWorkflows: number;
  degradedWorkflows: number;
  seedTotal: number;
  seedSucceeded: number;
  seedFailed: number;
  teardownTotal: number;
  teardownSucceeded: number;
  teardownFailed: number;
  tests: Record<string, WorkflowTestEntry>;
};

export type WorkflowTestStreamMergeMeta = {
  streamSinceCursor: string | null;
  suiteLastEventAt: string | null;
  workflowLastEventAt: Record<string, string>;
  /** Last applied _seq per workflow for tiebreaking concurrent events */
  workflowLastSeq: Record<string, number>;
  /** Last applied _seq for suite-level events */
  suiteLastSeq: number | null;
};

export type WorkflowExecutionLiveStateResponse = {
  ok: boolean;
  asOf: string;
  execution: {
    id: string;
    status:
      | 'queued'
      | 'running'
      | 'completed'
      | 'completed_with_errors'
      | 'completed_with_inconclusive'
      | 'canceled'
      | 'timeout';
    totalWorkflows: number;
    completedWorkflows: number;
    failedWorkflows: number;
    inconclusiveWorkflows?: number;
    successfulWorkflows: number;
    skippedWorkflows?: number;
    waitingWorkflows?: number;
    quarantinedWorkflows?: number;
    degradedWorkflows?: number;
    startedAt: string | null;
    finishedAt: string | null;
    updatedAt: string;
  };
  runs: Array<{
    testRunId: string;
    workflowId: string;
    workflowRunId: string | null;
    status: WorkflowTestStatus;
    isQuarantined?: boolean;
    startedAt: string | null;
    finishedAt: string | null;
    durationMs: number | null;
    errorMessage: string | null;
    skipReason?: string | null;
    linkedIssueId: string | null;
    updatedAt: string;
    resumeAt?: string | null;
    segmentIndex?: number | null;
    totalSegments?: number | null;
  }>;
};

const TERMINAL_STATUSES = new Set<WorkflowTestStatus>([
  'success',
  'failed',
  'inconclusive',
  'skipped',
  'flaky_success',
]);

const toMs = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const maxCounter = (current: number, incoming: number) =>
  Number.isFinite(incoming) ? Math.max(current, incoming) : current;

export const createInitialWorkflowTestStreamState = (): WorkflowTestStreamState => ({
  suiteId: 'pending',
  status: 'idle',
  startedAt: null,
  finishedAt: null,
  durationMs: 0,
  totalWorkflows: 0,
  completedWorkflows: 0,
  failedWorkflows: 0,
  inconclusiveWorkflows: 0,
  successfulWorkflows: 0,
  skippedWorkflows: 0,
  quarantinedWorkflows: 0,
  degradedWorkflows: 0,
  seedTotal: 0,
  seedSucceeded: 0,
  seedFailed: 0,
  teardownTotal: 0,
  teardownSucceeded: 0,
  teardownFailed: 0,
  tests: {},
});

export const createInitialWorkflowTestStreamMergeMeta = (): WorkflowTestStreamMergeMeta => ({
  streamSinceCursor: null,
  suiteLastEventAt: null,
  workflowLastEventAt: {},
  workflowLastSeq: {},
  suiteLastSeq: null,
});

export const hydrateWorkflowTestStreamFromLiveState = (
  liveState: WorkflowExecutionLiveStateResponse
): { state: WorkflowTestStreamState; meta: WorkflowTestStreamMergeMeta } => {
  const tests: Record<string, WorkflowTestEntry> = {};
  const workflowLastEventAt: Record<string, string> = {};

  for (const run of liveState.runs) {
    tests[run.workflowId] = {
      workflowId: run.workflowId,
      status: run.status,
      startedAt: run.startedAt,
      finishedAt: run.finishedAt,
      durationMs: run.durationMs ?? 0,
      errorMessage: run.errorMessage,
      skipReason: run.skipReason ?? null,
      linkedIssueId: run.linkedIssueId,
      isQuarantined: run.isQuarantined ?? false,
      testRunId: run.testRunId,
      workflowRunId: run.workflowRunId,
      message: null,
      lastUpdatedAt: run.updatedAt,
      resumeAt: run.resumeAt ?? null,
      segmentIndex: run.segmentIndex ?? null,
      totalSegments: run.totalSegments ?? null,
    };
    workflowLastEventAt[run.workflowId] = run.updatedAt;
  }

  const state: WorkflowTestStreamState = {
    suiteId: liveState.execution.id,
    status:
      liveState.execution.status === 'completed' ||
      liveState.execution.status === 'completed_with_errors' ||
      liveState.execution.status === 'completed_with_inconclusive' ||
      liveState.execution.status === 'canceled' ||
      liveState.execution.status === 'timeout'
        ? 'completed'
        : 'running',
    startedAt: liveState.execution.startedAt,
    finishedAt: liveState.execution.finishedAt,
    durationMs: 0,
    totalWorkflows: liveState.execution.totalWorkflows,
    completedWorkflows: liveState.execution.completedWorkflows,
    failedWorkflows: liveState.execution.failedWorkflows,
    inconclusiveWorkflows: liveState.execution.inconclusiveWorkflows ?? 0,
    successfulWorkflows: liveState.execution.successfulWorkflows,
    skippedWorkflows: liveState.execution.skippedWorkflows ?? 0,
    quarantinedWorkflows: liveState.execution.quarantinedWorkflows ?? 0,
    degradedWorkflows: liveState.execution.degradedWorkflows ?? 0,
    seedTotal: 0,
    seedSucceeded: 0,
    seedFailed: 0,
    teardownTotal: 0,
    teardownSucceeded: 0,
    teardownFailed: 0,
    tests,
  };

  const meta: WorkflowTestStreamMergeMeta = {
    streamSinceCursor: liveState.asOf,
    suiteLastEventAt: liveState.execution.updatedAt,
    workflowLastEventAt,
    workflowLastSeq: {},
    suiteLastSeq: null,
  };

  return { state, meta };
};

export const applyWorkflowTestStreamEvent = (
  state: WorkflowTestStreamState,
  meta: WorkflowTestStreamMergeMeta,
  payload: WorkflowTestStreamEvent
): { state: WorkflowTestStreamState; meta: WorkflowTestStreamMergeMeta } => {
  if (payload.type === 'workflow-test') {
    const eventAt = payload.emittedAt ?? null;
    const eventAtMs = toMs(eventAt);
    const lastAppliedMs = toMs(meta.workflowLastEventAt[payload.workflowId] ?? null);
    const hasCursor = Boolean(meta.streamSinceCursor);

    if (hasCursor && !eventAtMs) {
      return { state, meta };
    }
    if (eventAtMs !== null && lastAppliedMs !== null && eventAtMs < lastAppliedMs) {
      return { state, meta };
    }
    // Use _seq as tiebreaker when timestamps match
    const eventSeq = payload._seq ?? null;
    const lastSeq = meta.workflowLastSeq[payload.workflowId] ?? null;
    if (
      eventAtMs !== null &&
      lastAppliedMs !== null &&
      eventAtMs === lastAppliedMs &&
      eventSeq !== null &&
      lastSeq !== null &&
      eventSeq <= lastSeq
    ) {
      return { state, meta };
    }

    const existing = state.tests[payload.workflowId];
    const incomingStatus = payload.status ?? existing?.status ?? 'queued';
    const nextStatus =
      existing && TERMINAL_STATUSES.has(existing.status) && !TERMINAL_STATUSES.has(incomingStatus)
        ? existing.status
        : incomingStatus;

    const nextState: WorkflowTestStreamState = {
      ...state,
      suiteId: payload.suiteId,
      status: state.status === 'idle' ? 'running' : state.status,
      tests: {
        ...state.tests,
        [payload.workflowId]: {
          workflowId: payload.workflowId,
          status: nextStatus,
          startedAt: payload.startedAt ?? existing?.startedAt ?? null,
          finishedAt: payload.finishedAt ?? existing?.finishedAt ?? null,
          durationMs: payload.durationMs ?? existing?.durationMs ?? 0,
          errorMessage: payload.errorMessage ?? existing?.errorMessage ?? null,
          skipReason: payload.skipReason ?? existing?.skipReason ?? null,
          linkedIssueId: payload.linkedIssueId ?? existing?.linkedIssueId ?? null,
          health: payload.health ?? existing?.health,
          testRunId: payload.testRunId,
          workflowRunId: payload.workflowRunId ?? existing?.workflowRunId ?? null,
          message: payload.message ?? existing?.message ?? null,
          lastUpdatedAt: eventAt ?? existing?.lastUpdatedAt ?? new Date().toISOString(),
          resumeAt: payload.resumeAt ?? existing?.resumeAt ?? null,
          segmentIndex: payload.segmentIndex ?? existing?.segmentIndex ?? null,
          totalSegments: payload.totalSegments ?? existing?.totalSegments ?? null,
        },
      },
    };

    return {
      state: nextState,
      meta: {
        ...meta,
        workflowLastEventAt:
          eventAtMs !== null
            ? { ...meta.workflowLastEventAt, [payload.workflowId]: eventAt! }
            : meta.workflowLastEventAt,
        workflowLastSeq:
          eventSeq !== null
            ? { ...meta.workflowLastSeq, [payload.workflowId]: eventSeq }
            : meta.workflowLastSeq,
      },
    };
  }

  const eventAt = payload.emittedAt ?? null;
  const eventAtMs = toMs(eventAt);
  const suiteLastMs = toMs(meta.suiteLastEventAt);
  const hasCursor = Boolean(meta.streamSinceCursor);
  if (hasCursor && !eventAtMs) {
    return { state, meta };
  }
  if (eventAtMs !== null && suiteLastMs !== null && eventAtMs < suiteLastMs) {
    return { state, meta };
  }
  // Use _seq as tiebreaker when timestamps match
  const suiteEventSeq = payload._seq ?? null;
  if (
    eventAtMs !== null &&
    suiteLastMs !== null &&
    eventAtMs === suiteLastMs &&
    suiteEventSeq !== null &&
    meta.suiteLastSeq !== null &&
    suiteEventSeq <= meta.suiteLastSeq
  ) {
    return { state, meta };
  }

  const mappedStatus: WorkflowTestStreamState['status'] =
    payload.status === 'completed'
      ? 'completed'
      : payload.status === 'running'
        ? 'running'
        : 'idle';
  const nextSuiteStatus =
    state.status === 'completed' && mappedStatus !== 'completed' ? 'completed' : mappedStatus;

  const nextState: WorkflowTestStreamState = {
    ...state,
    suiteId: payload.suiteId,
    status: nextSuiteStatus,
    startedAt: payload.startedAt ?? state.startedAt,
    finishedAt: payload.finishedAt ?? state.finishedAt,
    durationMs: Math.max(state.durationMs, payload.durationMs ?? state.durationMs),
    totalWorkflows: maxCounter(state.totalWorkflows, payload.totalWorkflows),
    completedWorkflows: maxCounter(state.completedWorkflows, payload.completedWorkflows),
    failedWorkflows: maxCounter(state.failedWorkflows, payload.failedWorkflows),
    inconclusiveWorkflows: maxCounter(state.inconclusiveWorkflows, payload.inconclusiveWorkflows),
    successfulWorkflows: maxCounter(state.successfulWorkflows, payload.successfulWorkflows),
    skippedWorkflows: maxCounter(
      state.skippedWorkflows,
      payload.skippedWorkflows ?? state.skippedWorkflows
    ),
    quarantinedWorkflows: maxCounter(
      state.quarantinedWorkflows,
      payload.quarantinedWorkflows ?? state.quarantinedWorkflows
    ),
    degradedWorkflows: maxCounter(
      state.degradedWorkflows,
      payload.degradedWorkflows ?? state.degradedWorkflows
    ),
    seedTotal: maxCounter(state.seedTotal, payload.seedTotal ?? state.seedTotal),
    seedSucceeded: maxCounter(state.seedSucceeded, payload.seedSucceeded ?? state.seedSucceeded),
    seedFailed: maxCounter(state.seedFailed, payload.seedFailed ?? state.seedFailed),
    teardownTotal: maxCounter(state.teardownTotal, payload.teardownTotal ?? state.teardownTotal),
    teardownSucceeded: maxCounter(
      state.teardownSucceeded,
      payload.teardownSucceeded ?? state.teardownSucceeded
    ),
    teardownFailed: maxCounter(
      state.teardownFailed,
      payload.teardownFailed ?? state.teardownFailed
    ),
  };

  return {
    state: nextState,
    meta: {
      ...meta,
      suiteLastEventAt: eventAtMs !== null ? eventAt : meta.suiteLastEventAt,
      suiteLastSeq: suiteEventSeq !== null ? suiteEventSeq : meta.suiteLastSeq,
    },
  };
};
